<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from "vue";
import { isEmpty } from "@/helpers/utility/Text";

import PopupDialog from "@core/ui/PopupDialog.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import ConditionLabel from "../condition/ConditionLabel.vue";

import { useScenarioEndStore } from "./scenarioEnds.store";
import { useImageStore } from "../image/images.store";
import { useConditionStore } from "../condition/conditions.store";

interface Props {
  scenarioEndId: number;
}
const props = defineProps<Props>();
defineEmits<{
  (event: "close"): void;
}>();

const confirmButton = ref<HTMLButtonElement>();

const scenarioEndStore = useScenarioEndStore();
const imageStore = useImageStore();
const conditionStore = useConditionStore();

const scenarioEnd = computed(() => scenarioEndStore.items[props.scenarioEndId]);

const image = computed(() => {
  if (scenarioEnd.value?.imageId == null) return null;
  return imageStore.items[scenarioEnd.value.imageId];
});

const imageData = computed(() => {
  if (image.value == null) return null;
  return imageStore.mediaData[image.value.id];
});

const conditions = computed(() => {
  return conditionStore.getBySource(scenarioEnd.value);
});

onMounted(() => {
  nextTick(() => {
    confirmButton.value?.focus();
  });
});
</script>

<template>
  <popup-dialog :title="$t('messages.simulationEnded')" class="popup-info" :width="image ? 1000 : 700">
    <div class="popup-info__wrapper">
      <img
        v-if="image && imageData"
        class="popup-info__image"
        :src="imageData"
        :style="{ objectFit: image.objectFit }"
      />
      <div class="popup-info__content">
        <h2 class="title popup-info__title">
          {{ scenarioEnd.title || $t("messages.simulationEnded") }}
        </h2>

        <div class="popup-info__areas">
          <div
            v-if="!isEmpty(scenarioEnd.description)"
            class="popup-info__area section"
          >
            <formatted-text :value="scenarioEnd.description" />
          </div>
          <div v-else class="popup-info__area section">
            <div class="column">
              <condition-label
                v-for="condition of conditions"
                :key="condition.id"
                :condition-id="condition.id"
              />
            </div>
          </div>
        </div>

        <div class="popup-info__footer">
          <div></div>
          <button
            ref="confirmButton"
            class="button"
            type="button"
            @click.prevent="$emit('close')"
          >
            {{ $t("labels.ok") }}
          </button>
        </div>
      </div>
    </div>
  </popup-dialog>
</template>
